// Thin JSON client for the HTTP API. Authorization is enforced by the server;
// the UI only hides what a role can't use.

import type {
  ActivityList, CardLinks, CardReview, Connections, HomeSummary, Me, PlaidItemDisconnected, PlaidItemPurged, PlaidLinkExchange, PlaidLinkFinish, PlaidLinkRequest, PlaidLinkStart, PlaidLinkUpdated,
  ReviewItem, ReviewList, StatementDetail, StatementSettingsInput, StatementSettingsView,
  UnmatchedFilters, UnmatchedList, WiseConnected, WiseDisconnected,
} from '../../services/src/shared/api-types.ts';
import type { Auth } from './auth.ts';

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export class Api {
  private readonly base: string;
  private readonly auth: Auth;
  private readonly mockRole: string | null;

  constructor(base: string, auth: Auth, mockRole: string | null = null) {
    this.base = base.replace(/\/$/, '');
    this.auth = auth;
    this.mockRole = mockRole;
  }

  private async request<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown, retried = false): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await this.auth.token();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (this.mockRole) headers['X-Mock-Role'] = this.mockRole;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(this.base + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    if (res.status === 401 && !retried && (await this.auth.renew())) return this.request<T>(method, path, body, true);
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 403) throw new ApiError(403, 'Not allowed');
    if (!res.ok) throw new ApiError(res.status, json.error ?? `Request failed (${res.status})`);
    return json as T;
  }

  /** The access token carries no email, so it comes from the ID token's profile when the API has none. Never the sub. */
  me = async (): Promise<Me> => {
    const me = await this.request<Me>('GET', '/me');
    return { ...me, email: me.email ?? this.auth.profile().email };
  };
  home = () => this.request<HomeSummary>('GET', '/home');
  review = (status = 'pending') => this.request<ReviewList>('GET', `/review?status=${encodeURIComponent(status)}`);
  reviewCards = () => this.request<CardReview>('GET', '/review/cards');
  statement = (accountId: string, closesOn: string) =>
    this.request<StatementDetail>('GET', `/statements/${encodeURIComponent(accountId)}?closes=${encodeURIComponent(closesOn)}`);
  setStatementDays = (accountId: string, input: StatementSettingsInput) =>
    this.request<StatementSettingsView>('POST', `/accounts/${encodeURIComponent(accountId)}/statement`, input);
  approve = (id: string, note?: string) => this.request<ReviewItem>('POST', `/review/${encodeURIComponent(id)}/approve`, note ? { note } : {});
  reject = (id: string, note?: string) => this.request<ReviewItem>('POST', `/review/${encodeURIComponent(id)}/reject`, note ? { note } : {});
  unmatched = (filters: UnmatchedFilters) => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]);
    return this.request<UnmatchedList>('GET', `/unmatched${q.size ? `?${q}` : ''}`);
  };
  connections = () => this.request<Connections>('GET', '/connections');
  /** A Link token: embedded (in-page modal) by default, or hosted (a URL to send). With an itemId: reconnect that login (update mode). */
  startPlaidLink = (req: PlaidLinkRequest = {}) => this.request<PlaidLinkStart>('POST', '/plaid/link', req);
  /** Embedded: the new login from Link's onSuccess. */
  exchangePlaidLink = (publicToken: string, linkToken: string, institutionName?: string) =>
    this.request<PlaidLinkExchange>('POST', '/plaid/link/exchange', { publicToken, linkToken, ...(institutionName ? { institutionName } : {}) });
  /** Embedded reconnect finished (update mode sends no public token). */
  plaidLinkUpdated = (itemId: string, linkToken?: string) => this.request<PlaidLinkUpdated>('POST', '/plaid/link/updated', { itemId, ...(linkToken ? { linkToken } : {}) });
  /** Hosted link: after the person finished Plaid's page. */
  finishPlaidLink = (linkToken: string) => this.request<PlaidLinkFinish>('POST', '/plaid/link/finish', { linkToken });
  /** Stops syncing a login; its accounts and lines stay. */
  disconnectPlaidItem = (itemId: string) => this.request<PlaidItemDisconnected>('POST', `/plaid/items/${encodeURIComponent(itemId)}/disconnect`, {});
  /** Dev only: deletes a disconnected login's test data. */
  purgePlaidItem = (itemId: string) => this.request<PlaidItemPurged>('POST', `/plaid/items/${encodeURIComponent(itemId)}/purge`, {});
  /** Replaced cards: links, the sync's suggestions, and methods no connected account carries. */
  cardLinks = () => this.request<CardLinks>('GET', '/card-links');
  /** The method is now charged on this account; applies on the next sync. */
  linkCard = (method: string, accountId: string) => this.request<CardLinks>('POST', '/card-links', { method, accountId });
  unlinkCard = (method: string) => this.request<CardLinks>('DELETE', '/card-links', { method });
  /** "Not this card": the suggestion isn't made again. */
  dismissCardLink = (method: string, accountId: string) => this.request<CardLinks>('POST', '/card-links/dismiss', { method, accountId });
  /** Checks the token with Wise and stores it; the next sync reads Wise. The token is never sent back. */
  connectWise = (token: string) => this.request<WiseConnected>('POST', '/wise/token', { token });
  disconnectWise = () => this.request<WiseDisconnected>('POST', '/wise/disconnect', {});
  activity = (before?: string) => this.request<ActivityList>('GET', `/activity${before ? `?before=${encodeURIComponent(before)}` : ''}`);
}
